import React, { useState } from 'react';
import { X, MapPin, Smartphone, Check, ChevronRight, ChevronLeft, Loader2, ShieldCheck, Award, FileText, CreditCard } from 'lucide-react';
import { publicApi } from '../lib/api';
import { useCart } from '../context/CartContext';
import { LoyaltyWidget, CouponInput } from './LoyaltyWidget';
import TikTokConsentModal from './TikTokConsentModal';

const PROVIDERS = [
  { id: 'mtn', name: 'MTN Money', color: '#FFCC00', textColor: '#000' },
  { id: 'moov', name: 'Moov Money', color: '#0066B3', textColor: '#fff' },
  { id: 'celtis', name: 'Celtiis Cash', color: '#E31E24', textColor: '#fff' }
];

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);
const DELIVERY_FEE = 2000;

const CheckoutModal = ({ open, onClose }) => {
  const { items, totalAmount, clear } = useCart();
  const [step, setStep] = useState(1);
  const [address, setAddress] = useState({ fullName: '', email: '', phone: '', address: '', city: 'Cotonou', notes: '' });
  const [order, setOrder] = useState(null);
  const [provider, setProvider] = useState('mtn');
  const [payPhone, setPayPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payResult, setPayResult] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [payingFedapay, setPayingFedapay] = useState(false);

  const subtotal = totalAmount + DELIVERY_FEE;
  const discount = appliedCoupon ? Math.floor((subtotal * appliedCoupon.discount) / 100) : 0;
  const total = subtotal - discount;
  const VAT_RATE = 0.18;
  const totalHT = Math.round(total / (1 + VAT_RATE));
  const totalVAT = total - totalHT;

  const startFedaPay = () => setConsentOpen(true);
  const handleConsent = async (accepted) => {
    setConsentOpen(false);
    setPayingFedapay(true); setError('');
    try {
      const res = await publicApi.initFedaPay('order', order.reference, accepted);
      window.location.href = res.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur paiement FedaPay');
      setPayingFedapay(false);
    }
  };

  if (!open) return null;

  const canNext1 = address.fullName && /\S+@\S+\.\S+/.test(address.email) && address.phone.length >= 8 && address.address && address.city;

  const submitOrder = async () => {
    setLoading(true); setError('');
    try {
      const res = await publicApi.createOrder({
        items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })),
        address, subtotal: totalAmount, delivery_fee: DELIVERY_FEE, total: subtotal
      });
      setOrder(res);
      setPayPhone(address.phone);
      setStep(2);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const submitPayment = async () => {
    if (payPhone.length < 8) return setError('Numéro invalide');
    setLoading(true); setError('');
    try {
      const res = await publicApi.payOrder({ order_id: order.id, provider, phone: payPhone, coupon_code: appliedCoupon?.code });
      setPayResult(res);
      if (res.success) { setStep(3); clear(); } else { setError(res.message); }
    } catch (e) { setError('Erreur paiement'); } finally { setLoading(false); }
  };

  const handleClose = () => {
    setTimeout(() => {
      setStep(1); setAddress({ fullName: '', email: '', phone: '', address: '', city: 'Cotonou', notes: '' });
      setOrder(null); setPayResult(null); setPayPhone(''); setError('');
    }, 300);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end md:items-center justify-center animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white w-full md:max-w-2xl md:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom-6 duration-500">
        <div className="bg-gradient-to-br from-[#F4A340] to-[#E89534] px-6 py-5 text-white">
          <button onClick={handleClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold">Finaliser ma commande</h2>
          <p className="text-white/90 text-sm mt-1">Étape {Math.min(step, 2)} sur 2 · Paiement Mobile Money</p>
          <div className="flex items-center gap-2 mt-4">
            {[1, 2].map((s) => <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? 'bg-white' : 'bg-white/30'}`} />)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Récapitulatif</div>
                {items.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm py-1">
                    <span className="text-slate-700">{it.qty} × {it.name}</span>
                    <span className="font-semibold">{formatFCFA(it.qty * it.price)} F</span>
                  </div>
                ))}
                <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between text-sm text-slate-500">
                  <span>Livraison Cotonou</span><span>{formatFCFA(DELIVERY_FEE)} F</span>
                </div>
                <div className="flex justify-between font-bold pt-1"><span>Total</span><span className="text-[#E89534]">{formatFCFA(total)} F</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-600 pt-1">
                    <span>Réduction fidélité ({appliedCoupon.code})</span>
                    <span>-{formatFCFA(discount)} F</span>
                  </div>
                )}
              </div>

              <LoyaltyWidget email={address.email} onCouponPick={(code) => setAppliedCoupon({ code, discount: 0, pending: true })} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input placeholder="Nom complet *" value={address.fullName} onChange={(e) => setAddress({ ...address, fullName: e.target.value })} className="px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
                <input type="email" placeholder="Email *" value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })} className="px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
                <input placeholder="Téléphone *" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
                <input placeholder="Ville *" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
              </div>
              <div className="relative">
                <MapPin className="absolute left-4 top-4 w-4 h-4 text-slate-400" />
                <textarea placeholder="Adresse complète (quartier, rue, repère) *" rows={2} value={address.address} onChange={(e) => setAddress({ ...address, address: e.target.value })} className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none resize-none" />
              </div>
              <textarea placeholder="Notes de livraison (optionnel)" rows={2} value={address.notes} onChange={(e) => setAddress({ ...address, notes: e.target.value })} className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none resize-none" />

              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                <Award className="w-4 h-4 flex-shrink-0" />
                Cet achat vous rapportera <b className="mx-1">{Math.floor(total / 1000)} points</b> fidélité !
              </div>
            </div>
          )}

          {step === 2 && order && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
                <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-900">Commande créée · {order.reference}</div>
                  <div className="text-sm text-emerald-800">Complétez le paiement pour confirmer.</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Opérateur Mobile Money</label>
                <div className="grid grid-cols-3 gap-3">
                  {PROVIDERS.map((p) => (
                    <button key={p.id} onClick={() => setProvider(p.id)} className={`p-4 rounded-2xl border-2 text-center ${provider === p.id ? 'border-[#F4A340] shadow-md scale-[1.02]' : 'border-slate-200 hover:border-[#F4A340]/50'}`}>
                      <div className="w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center font-bold text-xs" style={{ backgroundColor: p.color, color: p.textColor }}>{p.id.toUpperCase()}</div>
                      <div className="text-xs font-semibold text-slate-700">{p.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2"><Smartphone className="w-4 h-4 inline mr-1" /> Numéro mobile</label>
                <input type="tel" value={payPhone} onChange={(e) => setPayPhone(e.target.value)} placeholder="+229 90 00 00 00" className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-lg font-medium" />
                <p className="text-xs text-slate-500 mt-2">Simulation démo · aucune vraie transaction</p>
              </div>

              <div className="bg-slate-900 text-white rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2"><span className="text-white/70 text-sm">Montant à payer</span><span className="text-xs px-2 py-0.5 bg-white/10 rounded-full uppercase tracking-wider">Sécurisé</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-300 mb-1"><span>Réduction fidélité</span><span>-{formatFCFA(discount)} F</span></div>
                )}
                <div className="border-t border-white/20 pt-2 mt-2 space-y-1 text-sm" data-testid="vat-breakdown">
                  <div className="flex justify-between text-white/70"><span>Montant HT</span><span>{formatFCFA(totalHT)} F</span></div>
                  <div className="flex justify-between text-white/70"><span>TVA (18%)</span><span>{formatFCFA(totalVAT)} F</span></div>
                </div>
                <div className="text-3xl font-bold mt-3">{formatFCFA(total)} <span className="text-lg font-normal text-white/60">F CFA TTC</span></div>
              </div>

              <button
                onClick={startFedaPay}
                disabled={payingFedapay || !order}
                data-testid="fedapay-pay-btn"
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-[#F4A340] to-[#E89534] hover:from-[#E89534] hover:to-[#D48222] disabled:opacity-60 text-white font-bold rounded-2xl shadow-lg hover:-translate-y-0.5 transition"
              >
                {payingFedapay ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                Payer {formatFCFA(total)} F avec FedaPay
              </button>
              {error && <div className="text-red-600 text-sm text-center" data-testid="pay-error">{error}</div>}
              <div className="text-center text-xs text-slate-400">— ou paiement manuel (simulation) —</div>

              <CouponInput
                email={address.email}
                appliedCode={appliedCoupon?.code}
                discountPercent={appliedCoupon?.discount}
                onApply={(code, pct) => setAppliedCoupon({ code, discount: pct })}
                onRemove={() => setAppliedCoupon(null)}
              />
            </div>
          )}

          {step === 3 && payResult?.success && (
            <div className="text-center py-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-6 animate-in zoom-in duration-500">
                <Check className="w-10 h-10 text-emerald-600" strokeWidth={3} />
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-2">Commande confirmée !</h3>
              <p className="text-slate-600 mb-6">Vous serez contacté(e) sous 24h pour la livraison.</p>
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 text-left space-y-2 max-w-md mx-auto">
                <div className="flex justify-between"><span className="text-slate-500">Référence</span><span className="font-bold">{order?.reference}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Total payé</span><span className="font-bold text-[#E89534]">{formatFCFA(total)} F</span></div>
                <div className="flex justify-between border-t pt-2"><span className="text-slate-500">Réf paiement</span><span className="font-mono text-sm">{payResult.reference}</span></div>
                <div className="flex items-center gap-2 mt-3 p-2 bg-white rounded-lg"><Award className="w-4 h-4 text-[#E89534]" /><span className="text-sm">+{Math.floor(total / 1000)} points fidélité crédités</span></div>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                <a
                  href={publicApi.invoiceUrl(order?.reference)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-slate-200 hover:border-[#F4A340] hover:text-[#E89534] text-slate-900 font-semibold rounded-full transition-colors"
                >
                  <FileText className="w-4 h-4" /> Télécharger la facture
                </a>
                <a
                  href={`/suivi/${order?.reference}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full transition-colors"
                >
                  Suivre ma commande <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}

          {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
        </div>

        {step < 3 && (
          <div className="border-t border-slate-100 p-4 flex items-center justify-between gap-3 bg-white">
            {step > 1 ? <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 px-5 py-3 rounded-full text-slate-700 hover:bg-slate-100 font-medium"><ChevronLeft className="w-4 h-4" /> Retour</button> : <div />}
            {step === 1 && <button onClick={submitOrder} disabled={!canNext1 || loading} className="flex items-center gap-2 px-6 py-3 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 disabled:cursor-not-allowed text-white font-semibold rounded-full">{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Traitement...</> : <>Aller au paiement <ChevronRight className="w-4 h-4" /></>}</button>}
            {step === 2 && <button onClick={submitPayment} disabled={loading || !payPhone} className="flex items-center gap-2 px-6 py-3 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full">{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Traitement...</> : <>Payer {formatFCFA(total)} F <ChevronRight className="w-4 h-4" /></>}</button>}
          </div>
        )}
        {step === 3 && (
          <div className="border-t border-slate-100 p-4 flex justify-center"><button onClick={handleClose} className="px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full">Fermer</button></div>
        )}
      </div>
      <TikTokConsentModal open={consentOpen} onChoice={handleConsent} onClose={() => setConsentOpen(false)} />
    </div>
  );
};

export default CheckoutModal;
